import { AUTH0_CONFIG } from './constants';
import { isAuth0Enabled } from './storage.web';
import { getRegionFromHostname } from './region.web';
import type { Region } from './constants';

// Re-export for convenience
export { isAuth0Enabled } from './storage.web';

// Import type only - actual import is dynamic to avoid side effects
type CreateAuthClient = typeof import('@appcues/auth').default;
type AuthClient = ReturnType<CreateAuthClient>;

let authClient: AuthClient | null = null;
let currentRegion: Region | undefined;
let authClientPromise: Promise<AuthClient> | null = null;

/**
 * Initialize the auth client for legacy auth.
 * This MUST be called before any auth operations when using legacy auth.
 *
 * For apps with public routes (like mobile-builder's /image-service),
 * call this AFTER checking for public routes to avoid premature redirects.
 *
 * For backward compatibility, ensureAuthClient() will call this automatically
 * if not already initialized, so existing apps continue to work.
 *
 * NOTE: Legacy auth captures URL parameters (like apc_auth token from login redirect),
 * so this should be called early in the auth flow, just not at module load time.
 *
 * The @appcues/auth package is dynamically imported to avoid side effects
 * when this module is imported on public routes.
 */
export const initializeAuthClient = async (): Promise<void> => {
  if (!authClient && !(await isAuth0Enabled())) {
    const { default: createAuthClient } = await import('@appcues/auth');
    // @ts-expect-error - TypeScript types show 3 required args but implementation accepts 2
    authClient = createAuthClient(
      import.meta.env.CUSTOMER_API_URL as string,
      import.meta.env.AUTH_APP_URL as string
    );
  }
};

/**
 * Get the current region (only set when Auth0 is enabled)
 */
export const getRegion = (): Region | undefined => currentRegion;

const getAuthClient = async (): Promise<AuthClient> => {
  if (!authClient && (await isAuth0Enabled())) {
    currentRegion = getRegionFromHostname();
    const config = AUTH0_CONFIG[currentRegion];

    // Dynamically import createAuth0Client only when Auth0 is enabled
    const { createAuth0Client } = await import('@appcues/auth');

    authClient = createAuth0Client({
      domain: config.domain,
      clientId: config.clientId,
      audience: config.audience,
      baseUrl: config.baseUrl,
      crxId: import.meta.env.CRX_ID as string,
      redirectUri: window.location.origin,
      scope: 'openid profile email offline_access',
    });
  }
  return authClient!;
};

const ensureAuthClient = async (): Promise<AuthClient> => {
  if (!authClientPromise) {
    // Initialize legacy auth client if not already done (backward compatibility)
    await initializeAuthClient();
    authClientPromise = getAuthClient();
  }
  return authClientPromise;
};

type AuthClientFn<T, A extends unknown[]> = (
  auth: AuthClient,
  ...args: A
) => T | Promise<T>;

const withAuthClient =
  <T, A extends unknown[]>(fn: AuthClientFn<T, A>) =>
  async (...args: A): Promise<T> => {
    const auth = await ensureAuthClient();
    return fn(auth, ...args);
  };

/**
 * Verify logged in user or redirect back to login page.
 */
export const login = withAuthClient(async auth => {
  const [jwt, accountId, userId] = await Promise.all([
    auth.getToken(),
    auth.getAccountId(),
    auth.getUserId(),
  ]);
  return { jwt, accountId, userId, token: jwt };
});

/**
 * Logout user
 */
export const logout = withAuthClient(auth => auth.logout());

/**
 * Spoof into another account
 */
export const spoof = withAuthClient((auth, id: string) => auth.spoof(id));

/**
 * Despoof from current account
 */
export const despoof = withAuthClient(auth => auth.despoof());

/**
 * Whether user is currently spoofing
 */
export const isSpoofing = withAuthClient(auth => auth.isSpoofing());

/**
 * Switch account
 */
export const switchAccount = withAuthClient((auth, id: string) =>
  auth.switchAccount(id)
);

/**
 * Get a fresh token from the auth client.
 * Can be used as a TokenProvider for API clients.
 */
export const getToken = withAuthClient(auth => auth.getToken());

/**
 * Get user info from Auth0 userinfo endpoint.
 * Only available when Auth0 is enabled.
 */
export const getUserInfo = withAuthClient(async auth => {
  if (!(await isAuth0Enabled()) || !auth.getUserInfo) {
    throw new Error('getUserInfo is only available in Auth0 mode');
  }
  return auth.getUserInfo();
});

/**
 * Get user accounts from Auth0.
 * Only available when Auth0 is enabled.
 */
export const getUserAccounts = withAuthClient(async auth => {
  if (!(await isAuth0Enabled()) || !auth.getUserAccounts) {
    throw new Error('getUserAccounts is only available in Auth0 mode');
  }
  return auth.getUserAccounts();
});
